import {Injectable} from '@angular/core';
import {PageSize} from '../../shared/table-paginator/page-sizes.model';
import {TenantService} from '../../services/tenant.service';
import {User} from '../../models/user.model';
import {environment} from '../../../environments/environment';
import {StudentsGroup} from '../../models/students-group.model';
import {updateSchoolDetails} from '../../store/platform.actions';
import {Tenant, TenantInfo} from '../../models/tenant.model';
import {TenantsDBService} from '../../services/tenants-db.service';
import {Store} from '@ngrx/store';
import {AppState} from '../../store';
import {LoadingService} from '../../shared/services/loading.service';
import {first, Observable} from 'rxjs';
import {
  collection,
  collectionData,
  doc,
  docData,
  Firestore,
  getDoc,
  getDocs,
  orderBy,
  query,
  QueryConstraint,
  setDoc,
  where
} from '@angular/fire/firestore';
import {Auth, idToken} from '@angular/fire/auth';
import {UserActivityLog} from '../../models/user-activity-log.model';
import { Timestamp } from "@firebase/firestore";
import { StudentEnrollments } from '../../models/student-enrollment.model';
import { createFirestoreId } from '../../common/firestore-utils';
import { MessagesService } from '../../shared/services/messages.service';
import { LatestAssessmentAttempt } from '../../models/assessment/assessment-attempt.model';
import { Course } from '../../models/course.model';
import { Lesson } from '../../models/lesson.model';

@Injectable({
  providedIn: 'root'
})

export class StudentsService {

  private authJwtToken: string;

  constructor(
    private store: Store<AppState>,
    private loading: LoadingService,
    private firestore: Firestore,
    private tenant: TenantService,
    private afAuth: Auth,
    private tenantDBService: TenantsDBService,
    private messages: MessagesService,
  ) {
    idToken(afAuth).subscribe(jwt => this.authJwtToken = jwt);
  }

  async loadStudentsPage(pageNumber: number, pageSize: PageSize) {

    // Get all user list with orderBy userCreatedAt desc and email asc from firestore
    const constraints = [
      orderBy('userCreatedAt', 'desc'),
      orderBy('email', 'asc')
    ]

    const query$ = await getDocs(query(collection(this.firestore, `schools/${this.tenant.id}/users`), ...constraints))

    const orderedUsers = query$.docs.map(doc => {
      return {
        id: doc.id,
        ...doc.data()
      } as User
    })


    console.log("##orderedUsers", orderedUsers);

    // Filter out users with emails ending with '@onlinecoursehost.com' admin email
    const filteredUsers = orderedUsers.filter(
      (user) => user?.email && !user?.email?.endsWith('@onlinecoursehost.com')
    );

    console.log("##filteredUsers", filteredUsers);

    // Get the start index of the page
    const startIndex = (pageNumber - 1) * pageSize;

    // Get the end index of the page
    const endIndex = startIndex + pageSize;

    // Determine button disabled states based on page number
    // const isNextButtonDisabled = endIndex >= users.length;
    const currentPage = filteredUsers.slice(startIndex, endIndex);

    console.log("##currentPage", currentPage);

    // Determine button disabled states based on page number
    const isNextButtonDisabled = endIndex >= filteredUsers.length;
    const isPreviousButtonDisabled = startIndex === 0;

    // Return the current page of users with document id and button states
    return {
      users: currentPage.map(user => ({
        id: user.id,
        ...user,
      } as User)),
      isNextButtonDisabled,
      isPreviousButtonDisabled
    };

  }

  async loadGroupStudentsPage(pageNumber: number, pageSize: PageSize, groupId: string) {

    // Get particular group user list with orderBy userCreatedAt desc and email asc from firestore
    const constraints = [
      orderBy('userCreatedAt', 'desc'),
      orderBy('email', 'asc'),
      where('groupIds', 'array-contains', groupId)
    ]

    const query$ = await getDocs(query(collection(this.firestore, `schools/${this.tenant.id}/users`), ...constraints))

    const orderedUsers = query$.docs.map(doc => {
      return {
        id: doc.id,
        ...doc.data()
      } as User
    })


    console.log("###orderedUsers", orderedUsers);

    // Filter out users with emails ending with '@onlinecoursehost.com' admin email
    const filteredUsers = orderedUsers.filter(
      (user) => user?.email && !user?.email?.endsWith('@onlinecoursehost.com')
    );

    // Get the start index of the page
    const startIndex = (pageNumber - 1) * pageSize;

    // Get the end index of the page
    const endIndex = startIndex + pageSize;

    // Get the current page of users
    // const currentPage = users.slice(startIndex, endIndex);

    // Determine button disabled states based on page number
    // const isNextButtonDisabled = endIndex >= users.length;
    const currentPage = filteredUsers.slice(startIndex, endIndex);

    // Determine button disabled states based on page number
    const isNextButtonDisabled = endIndex >= filteredUsers.length;
    const isPreviousButtonDisabled = startIndex === 0;

    // Return the current page of users with document id and button states
    return {
      users: currentPage.map(user => ({
        id: user.id,
        ...user
      } as User)),
      isNextButtonDisabled,
      isPreviousButtonDisabled
    };

    // Return full list with out pagination for testing purpose only
    // return {
    //   users: filteredUsers.map(user => ({
    //     id: user.id,
    //     ...user.data()
    //   } as User)),
    //   isNextButtonDisabled: true,
    //   isPreviousButtonDisabled: true
    // };

  }

  async loadStudentActivityLogsPage(pageNumber: number, pageSize: PageSize, userId: string, filterByEvents?: string[], filterByCourses?: string[]) {

    // Get particular user activity logs with orderBy createdAt desc from firestore
    const constraints = [
      orderBy('createdAt', 'desc'),
      where('userId', '==', userId)
    ]

    const query$ = collectionData(query(collection(this.firestore, `schools/${this.tenant.id}/userActivityLog`), ...constraints), {idField: "id"})

    const orderedActivityLogs = await query$.pipe(first()).toPromise() as UserActivityLog[];

    // Filter out activity logs by event types and course ids if filterByEvents and filterByCourses are provided as input parameters to the method call
    let filteredActivityLogs = orderedActivityLogs;

    if (filterByEvents && filterByEvents.length > 0 && filterByCourses && filterByCourses.length > 0) {
      filteredActivityLogs = filteredActivityLogs.filter(
        (activityLog) => filterByEvents.includes(activityLog.eventType) && filterByCourses.includes(activityLog.courseId)
      );
    } else if (filterByEvents && filterByEvents.length > 0) {
      filteredActivityLogs = filteredActivityLogs.filter(
        (activityLog) => filterByEvents.includes(activityLog.eventType)
      );
    } else if (filterByCourses && filterByCourses.length > 0) {
      filteredActivityLogs = filteredActivityLogs.filter(
        (activityLog) => filterByCourses.includes(activityLog.courseId)
      );
    } else {
      filteredActivityLogs = orderedActivityLogs;
    }

    // Get the start index of the page
    const startIndex = (pageNumber - 1) * pageSize;

    // Get the end index of the page
    const endIndex = startIndex + pageSize;

    // Get the current page of userActivityLogs
    const currentPage = filteredActivityLogs.slice(startIndex, endIndex);

    // Determine button disabled states based on page number
    const isNextButtonDisabled = endIndex >= filteredActivityLogs.length;
    const isPreviousButtonDisabled = startIndex === 0;

    // Return the current page of userActivityLogs with document id and
    return {
      userActivityLogs: currentPage.map(userActivityLog => ({
        id: userActivityLog.id,
        ...userActivityLog
      } as UserActivityLog)),
      isNextButtonDisabled,
      isPreviousButtonDisabled
    };

    // Return full list with out pagination for testing purpose only
    // return {
    //   userActivityLogs: filteredActivityLogs.map(userActivityLog => ({
    //     id: userActivityLog.id,
    //     ...userActivityLog.data()
    //   } as UserActivityLog)),
    //   isNextButtonDisabled: true,
    //   isPreviousButtonDisabled: true
    // };

  }

 async loadTenantStudentGroups() {
  try {
   // Get tenant data from firestore
    const tenantDocRef = doc(this.firestore, `tenants/${this.tenant.id}`);
    const tenantDocSnap = await getDoc(tenantDocRef);
    if (!tenantDocSnap.exists()) {
      return [];
    }
    const tenantData = tenantDocSnap.data();
    const studentGroups: StudentsGroup[] = tenantData?.studentGroups || [];
    // Return studentGroups array
    return studentGroups;
  } catch (error) {
    console.log(`load Tenant Student Groups failed.`, error);
    return [];
  }
 }

  async getUserByTenantAndId(userId: string) {

    // Get user by tenant id and user id from firestore and return user data as User model object
    const user = await docData(doc(this.firestore, `schools/${this.tenant.id}/users/${userId}`), {idField:"id"}).pipe(first()).toPromise();

    // Return user with document id
    return {
      id: user.id,
      ...user
    } as User;

  }

  downloadSelectedStudents(data: any, groupId: string) {
    // Download selected students as csv file from api endpoint with idToken and studentList query params in url as query string parameters in new tab window
    if (typeof data === 'string') {
      window.open(environment.api.downloadStudentsUrl + `?idToken=${this.authJwtToken}&filterBy=${data}&groupId=${groupId}`, '_blank');
    } else {
      window.open(environment.api.downloadStudentsUrl + `?idToken=${this.authJwtToken}&studentList=${data}&groupId=${groupId}`, '_blank');
    }
  }

  async updateTenantGroupsTotalUsers(groups: StudentsGroup[], included_groups: StudentsGroup[], count: number, isAdd: boolean) {
    try {
      this.loading.loadingOn();

      // Update total users in each group
      included_groups.forEach((group) => {
        const index = groups.findIndex((_group) => _group.id === group.id);
        if (index > -1) {
          if (isAdd) {
            groups[index].totalUsers = groups[index].totalUsers + count;
          } else {
            if (groups[index].totalUsers > 0) {
              groups[index].totalUsers = groups[index].totalUsers - count;
            }
          }
        }
      });

      // Update tenant studentGroups in firestore
      await this.tenantDBService.updateTenant(this.tenant.id, {studentGroups: groups}).toPromise();

      const changes: Partial<TenantInfo> = {
        studentGroups: groups.map((item) => {
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            totalUsers: item.totalUsers,
            courseIds: item.courseIds
          };
        })
      };

      this.store.dispatch(updateSchoolDetails({ changes }));

      console.log(`Group total users updated successfully.`);
    } catch (error) {
      console.log(`Group total users update failed.`, error);
    } finally {
      this.loading.loadingOff();
    }

  }

  async updateTenantGroupTotalUsers(groups: StudentsGroup[], groupId: string, count: number, isAdd: boolean) {
    try {
      this.loading.loadingOn();

      // Get the index of the group
      const groupIndex = groups.findIndex(item => item.id === groupId);

      // Update the total users count of the group
      if (groupIndex > -1) {
        groups[groupIndex].totalUsers = isAdd ? groups[groupIndex].totalUsers + count : groups[groupIndex].totalUsers > 0 ? groups[groupIndex].totalUsers - count : 0;
      }

      // Update tenant studentGroups in firestore
      await this.tenantDBService.updateTenant(this.tenant.id, {studentGroups: groups}).toPromise();

      const changes: Partial<TenantInfo> = {
        studentGroups: groups.map((item) => {
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            totalUsers: item.totalUsers,
            courseIds: item.courseIds
          };
        })
      };

      this.store.dispatch(updateSchoolDetails({ changes }));

      console.log(`Group total users updated successfully.`);
    } catch (error) {
      console.log(`Group total users update failed.`, error);
    } finally {
      this.loading.loadingOff();
    }

  }

  async updateParticularTenantGroupsTotalUsers(tenantId: string, groups: StudentsGroup[], included_groups: StudentsGroup[], count: number, isAdd: boolean) {
    try {
      this.loading.loadingOn();

      // Update total users in each group
      included_groups.forEach((group) => {
        const index = groups.findIndex((_group) => _group.id === group.id);
        if (index > -1) {
          if (isAdd) {
            groups[index].totalUsers = groups[index].totalUsers + count;
          } else {
            if (groups[index].totalUsers > 0) {
              groups[index].totalUsers = groups[index].totalUsers - count;
            }
          }
        }
      });

      // Update particular tenant studentGroups in firestore
      await this.tenantDBService.updateTenant(tenantId, {studentGroups: groups}).toPromise();

      const changes: Partial<TenantInfo> = {
        studentGroups: groups.map((item) => {
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            totalUsers: item.totalUsers,
            courseIds: item.courseIds
          };
        })
      };

      this.store.dispatch(updateSchoolDetails({ changes }));

      console.log(`Group total users updated successfully.`);
    } catch (error) {
      console.log(`Group total users update failed.`, error);
    } finally {
      this.loading.loadingOff();
    }

  }

  async updateTenantGroupsTotalUsersBySyncAllUsersGroupIds(groups: StudentsGroup[]) {
    try {
      this.loading.loadingOn();

      // Get all users from firestore
      const users = await collectionData(collection(this.firestore, `schools/${this.tenant.id}/users`), {idField: "id"}).pipe(first()).toPromise() as User[];

      // Update total users in each group
      groups.forEach((group) => {
        const index = groups.findIndex((_group) => _group.id === group.id);
        if (index > -1) {
          groups[index].totalUsers = users.filter(user => {
            const userData = user;
            return userData && userData.groupIds && userData.groupIds.includes(group.id);
          }).length;
        }
      });

      // Update tenant studentGroups in firestore
      await this.tenantDBService.updateTenant(this.tenant.id, {studentGroups: groups}).toPromise();

      const changes: Partial<TenantInfo> = {
        studentGroups: groups.map((item) => {
          return {
            id: item.id,
            name: item.name,
            description: item.description,
            totalUsers: item.totalUsers,
            courseIds: item.courseIds
          };
        })
      };

      this.store.dispatch(updateSchoolDetails({ changes }));

      console.log(`Group total users updated successfully.`);
    } catch (error) {
      console.log(`Group total users update failed.`, error);
    } finally {
      this.loading.loadingOff();
    }
  }

  async updateUsersGroupIdsByFilterFlagAndReturnDocIdsInArray(FilterFlag: string, groupId: string): Promise<string[]> {
    // Define array to store users document ids
    const usersDocumentIds: string[] = [];

    if (FilterFlag === "all active" && groupId !== '') {
      // Get all where invitationStatus is active and groupIds array contains groupId from firestore and return users document ids in array
      const constraints = [
        where('invitationStatus', '==', 'active'),
        where('groupIds', 'array-contains', groupId)
      ]
      const usersSnapshot = await collectionData(query(collection(this.firestore, `schools/${this.tenant.id}/users`), ...constraints), {idField: "id"}).pipe(first()).toPromise() as User[];

      for (const user of usersSnapshot) {
        const userGroupIds = user.groupIds;
        const index = userGroupIds.indexOf(groupId);
        if (index > -1) {
          userGroupIds.splice(index, 1);

          await setDoc(doc(this.firestore, `schools/${this.tenant.id}/users/${user.id}`), { groupIds: userGroupIds }, { merge: true });

          usersDocumentIds.push(user.id);
        }
      }
    } else if (FilterFlag === "all invited" && groupId !== '') {
      // Get all where invitationStatus is invited and groupIds array contains groupId from firestore and return users document ids in array
      const constraints = [
        where('invitationStatus', '==', 'invited'),
        where('groupIds', 'array-contains', groupId)
      ]

      const usersSnapshot = await collectionData(query(collection(this.firestore, `schools/${this.tenant.id}/users`), ...constraints), {idField: "id"}).pipe(first()).toPromise() as User[];

      for (const user of usersSnapshot) {
        const userGroupIds = user.groupIds;
        const index = userGroupIds.indexOf(groupId);
        if (index > -1) {
          userGroupIds.splice(index, 1);

          await setDoc(doc(this.firestore, `schools/${this.tenant.id}/users/${user.id}`), { groupIds: userGroupIds }, { merge: true });

          usersDocumentIds.push(user.id);
        }
      }
    } else if (FilterFlag === "all select" && groupId !== '') {
      // Get all where groupIds array contains groupId from firestore and return users document ids in array
      const constraints = [
        where('groupIds', 'array-contains', groupId)
      ]
      const usersSnapshot = await collectionData(query(collection(this.firestore, `schools/${this.tenant.id}/users`), ...constraints), {idField: "id"}).pipe(first()).toPromise() as User[];

      for (const user of usersSnapshot) {
        const userGroupIds = user.groupIds;
        const index = userGroupIds.indexOf(groupId);
        if (index > -1) {
          userGroupIds.splice(index, 1);

          await setDoc(doc(this.firestore, `schools/${this.tenant.id}/users/${user.id}`), { groupIds: userGroupIds }, { merge: true });

          usersDocumentIds.push(user.id);
        }
      }
    }

    // Return users document ids in array
    return usersDocumentIds;
  }

  async updateUsersGroupIdsBySelectedUsersDocIdsArrayAndReturnDocIdsInArray(SelectedUsersDocIds: string[], groupId: string): Promise<string[]> {
    // Define array to store users document ids
    const usersDocumentIds: string[] = [];

    if (SelectedUsersDocIds.length > 0 && groupId !== '') {
      // Use Promise.all to wait for all asynchronous operations to complete
      await Promise.all(SelectedUsersDocIds.map(async (userId) => {
        // First get the user data from firestore
        const userSnapshot = await docData(doc(this.firestore, `schools/${this.tenant.id}/users/${userId}`), {idField: "id"}).pipe(first()).toPromise() as User;
        const user = userSnapshot;

        // Check if user data exists
        if (user) {
          const userGroupIds = user.groupIds;
          const index = userGroupIds.indexOf(groupId);

          if (index > -1) {
            userGroupIds.splice(index, 1);

            // Update user data in firestore with merge true option
            await setDoc(doc(this.firestore, `schools/${this.tenant.id}/users/${userId}`), { groupIds: userGroupIds }, { merge: true });

            usersDocumentIds.push(userId);
          }
        }
      }));
    }

    // Return users document ids in array
    return usersDocumentIds;
  }

  updateUsersGroupIds(groupId: string): Promise<void> {
    // Get all where groupIds array contains groupId from firestore
    return new Promise<void>((resolve, reject) => {
      const constraints: QueryConstraint[] = [
        where('groupIds', 'array-contains', groupId)
      ];

      (collectionData(query(collection(this.firestore, `schools/${this.tenant.id}/users`), ...constraints), {idField: "id"}).pipe(first()) as Observable<User[]>).subscribe(
        async (users) => {
          for (const user of users) {
            // Remove groupId in user groupIds array than set the updated user data in firestore again with merge true option to update only groupIds array in the user document
            const userGroupIds = user.groupIds;
            const index = userGroupIds.indexOf(groupId);
            if (index > -1) {
              userGroupIds.splice(index, 1);

              await setDoc(doc(this.firestore, `schools/${this.tenant.id}/users/${user.id}`), { groupIds: userGroupIds }, { merge: true });
            }
          }
          resolve(); // Resolve the promise once all operations are complete
        },
        (error) => {
          reject(error); // Reject the promise if there is an error
        }
      );
    });
  }

  // For student enrollments
  async updateUserPrivate(userId: string, props: any) {
    try {
      const userPrivateRef = doc(this.firestore, `schools/${this.tenant.id}/usersPrivate/${userId}`);
      await setDoc(userPrivateRef, props, { merge: true });
    } catch (error) {
      const message = `Error updating user private data for userId=${userId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async createStudentEnrollment(userId: string, courseId: string) {
    try {
      // Create a new student enrollment in firestore
      const studentEnrollmentId = createFirestoreId(this.firestore);
      const studentEnrollmentPath = `schools/${this.tenant.id}/studentEnrollments/${studentEnrollmentId}`;
      const collectionRef = doc(this.firestore, studentEnrollmentPath);

      const enrollmentDate = Timestamp.now();
      const studentEnrollmentData: StudentEnrollments = {
        id: studentEnrollmentId,
        courseId,
        userId,
        status: 'active',
        enrollmentDate: enrollmentDate,
        progress: 0,
      };

      await setDoc(collectionRef, studentEnrollmentData);

      // Handle time-limited access for manual enrollment
      await this.handleTimeLimitedAccessForManualEnrollment(userId, courseId, enrollmentDate);

      return studentEnrollmentData;
    } catch (error) {
      const message = `Error creating student enrollment for userId=${userId} and courseId=${courseId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async createBulkStudentEnrollments(userId: string, courseIds: string[]) {
    try {
      const studentEnrollments: StudentEnrollments[] = [];

      for (const courseId of courseIds) {
        // check if the enrollment already exists and skip if it does
        const constraints: QueryConstraint[] = [
          where('userId', '==', userId),
          where('courseId', '==', courseId),
          where('status', '==', 'active')
        ];

        const existingEnrollmentsQuery = query(
          collection(this.firestore, `schools/${this.tenant.id}/studentEnrollments`),
          ...constraints
        );

        const existingEnrollmentsSnapshot = await getDocs(existingEnrollmentsQuery);
        if (!existingEnrollmentsSnapshot.empty) {
          console.log(`Enrollment already exists for userId=${userId} and courseId=${courseId}`);
          continue; // Skip creating a new enrollment if it already exists
        }

        // Create a new student enrollment
        const studentEnrollment = await this.createStudentEnrollment(userId, courseId);
        studentEnrollments.push(studentEnrollment);
      }

      return studentEnrollments;
    } catch (error) {
      const message = `Error creating bulk student enrollments for userId=${userId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async updateStudentEnrollment(enrollmentId: string, props: Partial<StudentEnrollments>) {
    try {
      const enrollmentRef = doc(this.firestore, `schools/${this.tenant.id}/studentEnrollments/${enrollmentId}`);
      await setDoc(enrollmentRef, props, { merge: true });
    } catch (error) {
      const message = `Error updating student enrollment for enrollmentId=${enrollmentId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async getStudentEnrollmentsByUserId(userId: string): Promise<StudentEnrollments[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('userId', '==', userId)
      ];

      const studentEnrollmentsQuery = query(collection(this.firestore, `schools/${this.tenant.id}/studentEnrollments`), ...constraints);

      const studentEnrollments = await getDocs(studentEnrollmentsQuery);

      const enrollments = studentEnrollments.docs.map(doc => doc.data() as StudentEnrollments) || [];

      enrollments.sort((a, b) => (b.enrollmentDate?.seconds ?? 0) - (a.enrollmentDate?.seconds ?? 0));

      return enrollments;
    } catch (error) {
      const message = `Error loading enrollments for userId=${userId}`;
      console.error(message, error);
      this.messages.error(message);
      return [];
    }
  }

  async updateStudentEnrollmentByCourseAndUser(courseId: string, userId: string, props: Partial<StudentEnrollments>) {
    try {
      // Query for the enrollment with courseId, userId, and status = 'active'
      const constraints: QueryConstraint[] = [
        where('courseId', '==', courseId),
        where('userId', '==', userId),
        where('status', '==', 'active')
      ];
      const enrollmentsQuery = query(
        collection(this.firestore, `schools/${this.tenant.id}/studentEnrollments`),
        ...constraints
      );
      const enrollmentsSnapshot = await getDocs(enrollmentsQuery);

      if (!enrollmentsSnapshot.empty) {
        // Update all matching enrollments (usually only one)
        for (const docSnap of enrollmentsSnapshot.docs) {
          const enrollmentRef = doc(this.firestore, `schools/${this.tenant.id}/studentEnrollments/${docSnap.id}`);
          await setDoc(enrollmentRef, props, { merge: true });
        }
      } else {
        const message = `No active enrollment found for userId=${userId} and courseId=${courseId}`;
        console.warn(message);
        this.messages.error(message);
      }
    } catch (error) {
      const message = `Error updating student enrollment for userId=${userId} and courseId=${courseId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async updateDripBypassSetting(enrollmentId: string, dripBypassEnabled: boolean) {
    try {
      const enrollmentRef = doc(this.firestore, `schools/${this.tenant.id}/studentEnrollments/${enrollmentId}`);
      await setDoc(enrollmentRef, { dripBypassEnabled }, { merge: true });
      this.messages.success(`Drip schedule ${dripBypassEnabled ? 'bypassed' : 'enabled'} for this student.`);
    } catch (error) {
      const message = `Error updating drip bypass setting for enrollmentId=${enrollmentId}`;
      console.error(message, error);
      this.messages.error(message);
    }
  }

  async loadStudentAssessmentAttemptsPage(
    pageNumber: number,
    pageSize: PageSize,
    userId: string,
    sortBy: 'Completion Date' | 'Passed Attempts' | 'Failed Attempts',
    searchTerm: string = '',
    courseIds: string[] = []
  ) {
    // Build Firestore query constraints
    let constraints: QueryConstraint[] = [
      where('userId', '==', userId)
    ];

    if (sortBy === 'Completion Date') {
      constraints.push(orderBy('submittedAt', 'desc'));
    } else if (sortBy === 'Passed Attempts') {
      constraints.push(orderBy('passed', 'desc'), orderBy('submittedAt', 'desc'));
    } else if (sortBy === 'Failed Attempts') {
      constraints.push(orderBy('passed', 'asc'), orderBy('submittedAt', 'desc'));
    }

    // Get all latest assessment attempts
    const attemptsSnap = await getDocs(
      query(
        collection(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts`),
        ...constraints
      )
    );
    let orderedAttempts = attemptsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as LatestAssessmentAttempt[];

    // Deduplicate: keep only latest attempt per assessmentId
    const latestAttemptsMap = new Map<string, LatestAssessmentAttempt>();
    orderedAttempts.forEach(attempt => {
      const existing = latestAttemptsMap.get(attempt.assessmentId);
      if (
        !existing ||
        (attempt.submittedAt?.seconds ?? 0) > (existing.submittedAt?.seconds ?? 0)
      ) {
        latestAttemptsMap.set(attempt.assessmentId, attempt);
      }
    });
    orderedAttempts = Array.from(latestAttemptsMap.values());

    // Fetch user data for maxAssessmentAttemptsForUser
    let user: User | undefined;
    try {
      const userRef = doc(this.firestore, `schools/${this.tenant.id}/users/${userId}`);
      const userSnap = await getDoc(userRef);
      user = userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } as User : undefined;
    } catch {
      user = undefined;
    }

    orderedAttempts = await Promise.all(orderedAttempts.map(async attempt => {
      // Get course document
      let course: Course | undefined;
      try {
        const courseRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${attempt.courseId}`);
        const courseSnap = await getDoc(courseRef);
        course = courseSnap.exists() ? { id: courseSnap.id, ...courseSnap.data() } as Course : undefined;
      } catch {
        course = undefined;
      }

      // Get lesson document (assessmentId is lessonId)
      let lesson: Lesson | undefined;
      try {
        const lessonRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${attempt.courseId}/lessons/${attempt.assessmentId}`);
        const lessonSnap = await getDoc(lessonRef);
        lesson = lessonSnap.exists() ? { id: lessonSnap.id, ...lessonSnap.data() } as Lesson : undefined;
      } catch {
        lesson = undefined;
      }

      // Get maxAssessmentAttempts from lesson
      const lessonMaxAssessmentAttempts = lesson?.assessment?.maxAssessmentAttempts ?? 1;

      // Get user-specific maxAssessmentAttempts if present
      let maxAssessmentAttemptsForUser = lessonMaxAssessmentAttempts;
      if (user?.maxAssessmentAttempts && user.maxAssessmentAttempts[attempt.assessmentId]) {
        maxAssessmentAttemptsForUser = user.maxAssessmentAttempts[attempt.assessmentId];
      }

      const minimumPassingGrade = lesson?.assessment?.minimumPassingGrade ?? 0;

      const allowAssessmentRetries = lesson?.assessment?.allowAssessmentRetries ?? false;

      // Calculate correctAnswers for main attempt
      const totalAnswers = Array.isArray(attempt.answers) ? attempt.answers.length : 0;
      const correctCount = Array.isArray(attempt.answers)
        ? attempt.answers.filter(ans => ans.isCorrect === true).length
        : 0;
      const correctAnswers = `${correctCount}/${totalAnswers}`;

      // Map previousAttempts with new fields, sorted by submittedAt descending
      const previousAttemptsWithFields = (attempt.previousAttempts ?? [])
        .slice()
        .sort((a, b) => (b.submittedAt?.seconds ?? 0) - (a.submittedAt?.seconds ?? 0))
        .map(prev => {
          const prevTotalAnswers = Array.isArray(prev.answers) ? prev.answers.length : 0;
          const prevCorrectCount = Array.isArray(prev.answers)
            ? prev.answers.filter(ans => ans.isCorrect === true).length
            : 0;
          return {
            ...prev,
            // attempts: `${prev.attemptNumber}/${maxAssessmentAttemptsForUser}`,
            attempts: `${prev.attemptNumber}/${ allowAssessmentRetries ? maxAssessmentAttemptsForUser : prev.attemptNumber}`,
            maxAssessmentAttempts: lessonMaxAssessmentAttempts,
            maxAssessmentAttemptsForUser,
            allowAssessmentRetries,
            minimumPassingGrade,
            correctAnswers: `${prevCorrectCount}/${prevTotalAnswers}`
          };
        });

      return {
        ...attempt,
        courseTitle: course ? course.title : 'N/A',
        assessmentTitle: lesson ? lesson.title : 'N/A',
        // attempts: `${attempt.attemptNumber}/${maxAssessmentAttemptsForUser}`,
        attempts: `${attempt.attemptNumber}/${ allowAssessmentRetries ? maxAssessmentAttemptsForUser : attempt.attemptNumber}`,
        maxAssessmentAttempts: lessonMaxAssessmentAttempts,
        maxAssessmentAttemptsForUser,
        allowAssessmentRetries,
        minimumPassingGrade,
        correctAnswers,
        previousAttempts: previousAttemptsWithFields
      };
    }));

    // Filter: Only graded attempts (gradedAt present) and course/lesson present
    orderedAttempts = orderedAttempts.filter(attempt =>
      !!attempt.gradedAt &&
      (attempt as LatestAssessmentAttempt & { courseTitle: string, assessmentTitle: string }).courseTitle !== 'N/A' &&
      (attempt as LatestAssessmentAttempt & { courseTitle: string, assessmentTitle: string }).assessmentTitle !== 'N/A'
    );

    // Manual filtering by courseIds
    if (courseIds && courseIds.length > 0) {
      orderedAttempts = orderedAttempts.filter(attempt => courseIds.includes(attempt.courseId));
    }

    // Manual filtering by searchTerm (case-insensitive, debounce 500ms) on assessmentTitle
    if (searchTerm && searchTerm.trim() !== '') {
      await new Promise(resolve => setTimeout(resolve, 500));
      const lowerSearch = searchTerm.trim().toLowerCase();
      orderedAttempts = orderedAttempts.filter(attempt =>
        (attempt as LatestAssessmentAttempt & { assessmentTitle: string }).assessmentTitle.toLowerCase().includes(lowerSearch)
      );
    }

    // Pagination
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPage = orderedAttempts.slice(startIndex, endIndex);

    // Button states
    const isNextButtonDisabled = endIndex >= orderedAttempts.length;
    const isPreviousButtonDisabled = startIndex === 0;

    return {
      assessmentAttempts: currentPage.map(attempt => ({
        id: attempt.id,
        ...attempt
      })),
      isNextButtonDisabled,
      isPreviousButtonDisabled
    };
  }

  async loadStudentAssessmentAttemptsPageAll(
    pageNumber: number,
    pageSize: PageSize,
    userId: string,
    sortBy: 'Completion Date' | 'Passed Attempts' | 'Failed Attempts',
    searchTerm: string = '',
    courseIds: string[] = []
  ) {
    // Build Firestore query constraints
    let constraints: QueryConstraint[] = [
      where('userId', '==', userId)
    ];

    if (sortBy === 'Completion Date') {
      constraints.push(orderBy('submittedAt', 'desc'));
    } else if (sortBy === 'Passed Attempts') {
      constraints.push(orderBy('passed', 'desc'), orderBy('submittedAt', 'desc'));
    } else if (sortBy === 'Failed Attempts') {
      constraints.push(orderBy('passed', 'asc'), orderBy('submittedAt', 'desc'));
    }

    // Get all assessment attempts (no deduplication)
    const attemptsSnap = await getDocs(
      query(
        collection(this.firestore, `schools/${this.tenant.id}/latestAssessmentAttempts`),
        ...constraints
      )
    );
    let orderedAttempts = attemptsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as LatestAssessmentAttempt[];

    // Fetch user data for maxAssessmentAttemptsForUser
    let user: User | undefined;
    try {
      const userRef = doc(this.firestore, `schools/${this.tenant.id}/users/${userId}`);
      const userSnap = await getDoc(userRef);
      user = userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } as User : undefined;
    } catch {
      user = undefined;
    }

    orderedAttempts = await Promise.all(orderedAttempts.map(async attempt => {
      // Get course document
      let course: Course | undefined;
      try {
        const courseRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${attempt.courseId}`);
        const courseSnap = await getDoc(courseRef);
        course = courseSnap.exists() ? { id: courseSnap.id, ...courseSnap.data() } as Course : undefined;
      } catch {
        course = undefined;
      }

      // Get lesson document (assessmentId is lessonId)
      let lesson: Lesson | undefined;
      try {
        const lessonRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${attempt.courseId}/lessons/${attempt.assessmentId}`);
        const lessonSnap = await getDoc(lessonRef);
        lesson = lessonSnap.exists() ? { id: lessonSnap.id, ...lessonSnap.data() } as Lesson : undefined;
      } catch {
        lesson = undefined;
      }

      // Get maxAssessmentAttempts from lesson
      const lessonMaxAssessmentAttempts = lesson?.assessment?.maxAssessmentAttempts ?? 1;

      // Get user-specific maxAssessmentAttempts if present
      let maxAssessmentAttemptsForUser = lessonMaxAssessmentAttempts;
      if (user?.maxAssessmentAttempts && user.maxAssessmentAttempts[attempt.assessmentId]) {
        maxAssessmentAttemptsForUser = user.maxAssessmentAttempts[attempt.assessmentId];
      }

      const minimumPassingGrade = lesson?.assessment?.minimumPassingGrade ?? 0;

      const allowAssessmentRetries = lesson?.assessment?.allowAssessmentRetries ?? false;

      // Calculate correctAnswers for main attempt
      const totalAnswers = Array.isArray(attempt.answers) ? attempt.answers.length : 0;
      const correctCount = Array.isArray(attempt.answers)
        ? attempt.answers.filter(ans => ans.isCorrect === true).length
        : 0;
      const correctAnswers = `${correctCount}/${totalAnswers}`;

      // Map previousAttempts with new fields, sorted by submittedAt descending
      const previousAttemptsWithFields = (attempt.previousAttempts ?? [])
        .slice()
        .sort((a, b) => (b.submittedAt?.seconds ?? 0) - (a.submittedAt?.seconds ?? 0))
        .map(prev => {
          const prevTotalAnswers = Array.isArray(prev.answers) ? prev.answers.length : 0;
          const prevCorrectCount = Array.isArray(prev.answers)
            ? prev.answers.filter(ans => ans.isCorrect === true).length
            : 0;
          return {
            ...prev,
            // attempts: `${prev.attemptNumber}/${maxAssessmentAttemptsForUser}`,
            attempts: `${prev.attemptNumber}/${ allowAssessmentRetries ? maxAssessmentAttemptsForUser : prev.attemptNumber}`,
            maxAssessmentAttempts: lessonMaxAssessmentAttempts,
            maxAssessmentAttemptsForUser,
            allowAssessmentRetries,
            minimumPassingGrade,
            correctAnswers: `${prevCorrectCount}/${prevTotalAnswers}`
          };
        });

      return {
        ...attempt,
        courseTitle: course ? course.title : 'N/A',
        assessmentTitle: lesson ? lesson.title : 'N/A',
        // attempts: `${attempt.attemptNumber}/${maxAssessmentAttemptsForUser}`,
        attempts: `${attempt.attemptNumber}/${ allowAssessmentRetries ? maxAssessmentAttemptsForUser : attempt.attemptNumber}`,
        maxAssessmentAttempts: lessonMaxAssessmentAttempts,
        maxAssessmentAttemptsForUser,
        allowAssessmentRetries,
        minimumPassingGrade,
        correctAnswers,
        previousAttempts: previousAttemptsWithFields
      };
    }));

    // Filter: Only graded attempts (gradedAt present) and course/lesson present
    orderedAttempts = orderedAttempts.filter(attempt =>
      !!attempt.gradedAt &&
      (attempt as LatestAssessmentAttempt & { courseTitle: string, assessmentTitle: string }).courseTitle !== 'N/A' &&
      (attempt as LatestAssessmentAttempt & { courseTitle: string, assessmentTitle: string }).assessmentTitle !== 'N/A'
    );

    // Manual filtering by courseIds
    if (courseIds && courseIds.length > 0) {
      orderedAttempts = orderedAttempts.filter(attempt => courseIds.includes(attempt.courseId));
    }

    // Manual filtering by searchTerm (case-insensitive, debounce 500ms) on assessmentTitle
    if (searchTerm && searchTerm.trim() !== '') {
      await new Promise(resolve => setTimeout(resolve, 500));
      const lowerSearch = searchTerm.trim().toLowerCase();
      orderedAttempts = orderedAttempts.filter(attempt =>
        (attempt as LatestAssessmentAttempt & { assessmentTitle: string }).assessmentTitle.toLowerCase().includes(lowerSearch)
      );
    }

    // Pagination
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPage = orderedAttempts.slice(startIndex, endIndex);

    // Button states
    const isNextButtonDisabled = endIndex >= orderedAttempts.length;
    const isPreviousButtonDisabled = startIndex === 0;

    return {
      assessmentAttempts: currentPage.map(attempt => ({
        id: attempt.id,
        ...attempt
      })),
      isNextButtonDisabled,
      isPreviousButtonDisabled
    };
  }

  /**
   * Set maxAssessmentAttempts for a user and assessment to a specific value.
   */
  async setUserMaxAssessmentAttempts(
    userId: string,
    assessmentId: string,
    newValue: number
  ): Promise<void> {
    const userRef = doc(this.firestore, `schools/${this.tenant.id}/users/${userId}`);
    const userSnap = await getDoc(userRef);
    let maxAssessmentAttempts = {};

    if (userSnap.exists()) {
      const userData = userSnap.data() as User;
      maxAssessmentAttempts = { ...(userData.maxAssessmentAttempts || {}) };
      maxAssessmentAttempts[assessmentId] = newValue;
    } else {
      maxAssessmentAttempts = { [assessmentId]: newValue };
    }

    await setDoc(userRef, { maxAssessmentAttempts }, { merge: true });
  }

  /**
   * Handle time-limited access for manual enrollment by updating user's temporaryAccess
   */
  private async handleTimeLimitedAccessForManualEnrollment(userId: string, courseId: string, enrollmentDate: Timestamp) {
    try {
      // Get course data to check if it has time-limited access
      const courseRef = doc(this.firestore, `schools/${this.tenant.id}/courses/${courseId}`);
      const courseSnap = await getDoc(courseRef);
      
      if (!courseSnap.exists()) {
        console.log(`Course ${courseId} not found`);
        return;
      }

      const course = courseSnap.data() as Course;
      
      // Check if course has time-limited access enabled
      if (!course?.timeLimitedAccess?.enabled) {
        console.log(`Course ${courseId} does not have time-limited access enabled`);
        return;
      }

      // Calculate expiration date
      const expirationDate = this.getExpirationTimestamp(enrollmentDate, course.timeLimitedAccess.period, course.timeLimitedAccess.periodTime);
      
      if (!expirationDate) {
        console.log(`Could not calculate expiration date for course ${courseId}`);
        return;
      }

      // Get current user private data
      const userPrivateRef = doc(this.firestore, `schools/${this.tenant.id}/usersPrivate/${userId}`);
      const userPrivateSnap = await getDoc(userPrivateRef);
      
      let userPrivate = userPrivateSnap.exists() ? userPrivateSnap.data() : {};
      let temporaryAccess = userPrivate?.temporaryAccess || {};

      // Update temporaryAccess with the new expiration date
      temporaryAccess[courseId] = expirationDate;

      // Update user private document with temporaryAccess
      await setDoc(userPrivateRef, { temporaryAccess }, { merge: true });
      
      console.log(`Updated temporaryAccess for user ${userId} and course ${courseId} with expiration date: ${expirationDate.toDate()}`);
      
    } catch (error) {
      console.error(`Error handling time-limited access for manual enrollment:`, error);
    }
  }

  /**
   * Calculate expiration timestamp based on enrollment date and time-limited access settings
   */
  private getExpirationTimestamp(enrollmentDate: Timestamp, period: number, periodTime: string): Timestamp | null {
    if (!enrollmentDate || !period || !periodTime) return null;
    
    const futureDate = enrollmentDate.toDate();
    
    switch (periodTime) {
      case "day":
        futureDate.setDate(futureDate.getDate() + period);
        break;
      case "month":
        futureDate.setMonth(futureDate.getMonth() + period);
        break;
      case "year":
        futureDate.setFullYear(futureDate.getFullYear() + period);
        break;
      default:
        return null;
    }
    
    return Timestamp.fromDate(futureDate);
  }
}
